import type { ChatFileItem, ChatFileKind, ChatMessage } from '@skychat/shared-core';

function kindForMessage(message: ChatMessage, mime?: string): ChatFileKind {
  if (message.type === 'image' || message.type === 'video' || message.type === 'audio' || message.type === 'gif') return message.type;
  const normalizedMime = String(mime || '').toLowerCase();
  if (normalizedMime === 'image/gif') return 'gif';
  if (normalizedMime.startsWith('image/')) return 'image';
  if (normalizedMime.startsWith('video/')) return 'video';
  if (normalizedMime.startsWith('audio/')) return 'audio';
  if (message.type === 'file') return 'document';
  return 'other';
}

export function formatBytes(bytes?: number) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
}

export function chatFileToMessage(file: ChatFileItem): ChatMessage {
  const type = file.kind === 'image' || file.kind === 'gif'
    ? 'image'
    : file.kind === 'video'
      ? 'video'
      : file.kind === 'audio'
        ? 'audio'
        : 'file';
  return {
    id: file.messageId,
    fromId: file.senderId,
    toId: file.peerId,
    ts: file.ts,
    type,
    iv: file.iv || undefined,
    plain: file.name || '',
    file: {
      id: file.id,
      name: file.name,
      size: file.size,
      mime: file.mime,
      type: file.mime,
      url: file.url,
      unavailable: file.unavailable
    }
  };
}

export function isMediaKind(kind?: ChatFileKind) {
  return kind === 'image' || kind === 'video' || kind === 'gif';
}

export function chatMessageToFileItem(message: ChatMessage, peerId: string): ChatFileItem | null {
  if (!message.file || message.type === 'deleted') return null;
  const mime = message.file.mime || message.file.type || '';
  const id = String(message.file.id || message.id || '').trim();
  if (!id) return null;
  return {
    id,
    messageId: message.id,
    senderId: message.fromId,
    peerId,
    name: message.file.name || message.plain || undefined,
    mime,
    size: Number(message.file.size || 0),
    url: message.file.url,
    ts: Number(message.ts || 0),
    kind: kindForMessage(message, mime),
    iv: message.iv || null,
    unavailable: message.file.unavailable === true
  };
}

export function chatMessagesToFiles(messages: ChatMessage[], peerId: string) {
  return messages
    .map((message) => chatMessageToFileItem(message, peerId))
    .filter((file): file is ChatFileItem => !!file);
}

export function monthLabel(ts: number, locale?: string) {
  const date = new Date(ts || Date.now());
  return date.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'long' });
}

export function groupFilesByMonth<T extends { ts: number }>(files: T[], locale?: string) {
  const groups: Array<{ key: string; label: string; files: T[] }> = [];
  for (const file of files) {
    const date = new Date(file.ts || Date.now());
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = { key, label: monthLabel(file.ts, locale), files: [] };
      groups.push(group);
    }
    group.files.push(file);
  }
  return groups;
}

export function extractLinks(messages: Array<{ id: string; plain?: string; ts: number }>) {
  const links: Array<{ id: string; url: string; host: string; ts: number }> = [];
  const pattern = /https?:\/\/[^\s]+/gi;
  for (const message of messages) {
    const text = String(message.plain || '');
    const matches = text.match(pattern) || [];
    for (const url of matches) {
      let host = url;
      try {
        host = new URL(url).hostname;
      } catch {
        host = url;
      }
      links.push({ id: `${message.id}:${url}`, url, host, ts: message.ts });
    }
  }
  return links;
}
